import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import about from '@/mocks/zh/about';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormMessage(about.contact.form.success);
      form.reset();
      return;
    }

    const message = formData.get('message') as string;
    if (message && message.length > 500) {
      setFormStatus('error');
      setFormMessage('留言内容不能超过500字');
      return;
    }

    setFormStatus('submitting');

    try {
      const submitData = new URLSearchParams();
      submitData.append('name', (formData.get('name') as string) || '');
      submitData.append('email', (formData.get('email') as string) || '');
      submitData.append('phone', (formData.get('phone') as string) || '');
      submitData.append('subject', (formData.get('subject') as string) || '');
      submitData.append('message', message || '');

      const response = await fetch('https://readdy.ai/api/form/d9rfmjee3oq4jqumu0pg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: submitData.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage(about.contact.form.success);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '';
        setFormStatus('error');
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormMessage(about.contact.form.success);
        } else {
          setFormMessage(serverMsg || about.contact.form.error);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage(about.contact.form.error);
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <div className="pt-16">
        <Breadcrumb />
      </div>

      <main>
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                {about.brand.subtitle}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                {about.brand.title}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="lg:w-1/2">
                <div className="rounded-xl overflow-hidden aspect-[4/3] w-full">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20corporate%20office%20building%20exterior%20with%20warm%20golden%20sunset%20lighting%2C%20clean%20glass%20facade%2C%20professional%20and%20trustworthy%20atmosphere%2C%20architectural%20photography%2C%20no%20people%2C%20warm%20cream%20and%20amber%20tones&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="开云平台品牌形象"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:w-1/2">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">
                  {about.brand.title}
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">{about.brand.description1}</p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">{about.brand.description2}</p>
                <p className="text-sm text-foreground-600 leading-relaxed">{about.brand.description3}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/70 py-12 md:py-16">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {about.milestones.title}
              </h2>
              <p className="text-foreground-500 text-sm">{about.milestones.subtitle}</p>
            </div>
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute left-[19px] top-0 bottom-0 w-px bg-accent-200 hidden md:block" />
              <div className="flex flex-col gap-6">
                {about.milestones.items.map((item, index) => (
                  <div key={index} className="flex items-start gap-6">
                    <div className="hidden md:flex w-10 h-10 flex-shrink-0 items-center justify-center rounded-full bg-accent-100 z-10">
                      <div className="w-3 h-3 rounded-full bg-accent-500" />
                    </div>
                    <div className="flex-1 bg-background-50 rounded-xl p-5 border border-background-200/60">
                      <span className="inline-block text-xs px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 font-semibold mb-2">
                        {item.year}
                      </span>
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {about.reputation.title}
              </h2>
              <p className="text-foreground-500 text-sm">{about.reputation.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {about.reputation.items.map((item, index) => (
                <div key={index} className="p-6 rounded-xl bg-background-100/80 border border-background-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-sm ${i < item.rating ? 'ri-star-fill text-primary-400' : 'ri-star-fill text-foreground-200'}`} />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">"{item.content}"</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                    <p className="text-xs text-foreground-400">{item.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100/70 py-12 md:py-16">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                {about.contact.title}
              </h2>
              <p className="text-foreground-500 text-sm">{about.contact.subtitle}</p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-2/5">
                <div className="bg-background-50 rounded-xl p-6 border border-background-200/60">
                  <h3 className="font-heading font-semibold text-foreground-900 mb-6 text-lg">
                    {about.contact.info.title}
                  </h3>
                  <div className="flex flex-col gap-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-phone-line text-primary-600 text-lg" />
                      </div>
                      <div>
                        <span className="block text-xs text-foreground-400 mb-0.5">{about.contact.info.hotline}</span>
                        <span className="text-sm font-semibold text-foreground-900">{about.contact.info.hotlineValue}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-mail-line text-primary-600 text-lg" />
                      </div>
                      <div>
                        <span className="block text-xs text-foreground-400 mb-0.5">{about.contact.info.email}</span>
                        <span className="text-sm font-semibold text-foreground-900">{about.contact.info.emailValue}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-map-pin-line text-primary-600 text-lg" />
                      </div>
                      <div>
                        <span className="block text-xs text-foreground-400 mb-0.5">{about.contact.info.address}</span>
                        <span className="text-sm font-semibold text-foreground-900">{about.contact.info.addressValue}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                        <i className="ri-time-line text-primary-600 text-lg" />
                      </div>
                      <div>
                        <span className="block text-xs text-foreground-400 mb-0.5">{about.contact.info.hours}</span>
                        <span className="text-sm font-semibold text-foreground-900">{about.contact.info.hoursValue}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-3/5">
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/60"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {about.contact.form.name}
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        placeholder={about.contact.form.namePlaceholder}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {about.contact.form.email}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        placeholder={about.contact.form.emailPlaceholder}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {about.contact.form.phone}
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        placeholder={about.contact.form.phonePlaceholder}
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        {about.contact.form.subject}
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      >
                        <option value="">{about.contact.form.subjectPlaceholder}</option>
                        {about.contact.form.subjectOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {about.contact.form.message}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder={about.contact.form.messagePlaceholder}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    />
                    <p className="text-xs text-foreground-300 mt-1">最多可输入500字</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : about.contact.form.submit}
                  </button>

                  {formStatus === 'success' && (
                    <div className="mt-4 p-3 rounded-lg bg-accent-50 border border-accent-200">
                      <p className="text-sm text-accent-700 flex items-center gap-2">
                        <i className="ri-checkbox-circle-line" />
                        {formMessage}
                      </p>
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200">
                      <p className="text-sm text-red-700 flex items-center gap-2">
                        <i className="ri-error-warning-line" />
                        {formMessage}
                      </p>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}